import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type BreadcrumbItem = {
  label: string;
  path?: string;
  onClick?: () => void;
};

type BreadcrumbContextValue = {
  suffixItems: BreadcrumbItem[];
  setTabBreadcrumbSuffix: (items: BreadcrumbItem[]) => void;
  setDetailBreadcrumbSuffix: (items: BreadcrumbItem[]) => void;
  clearBreadcrumbSuffix: () => void;
  /** @deprecated Use setTabBreadcrumbSuffix / setDetailBreadcrumbSuffix */
  setBreadcrumbSuffix: (items: BreadcrumbItem[]) => void;
};

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export const BreadcrumbProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tabSuffixItems, setTabSuffixItems] = useState<BreadcrumbItem[]>([]);
  const [detailSuffixItems, setDetailSuffixItems] = useState<BreadcrumbItem[]>([]);

  const setTabBreadcrumbSuffix = useCallback((items: BreadcrumbItem[]) => {
    setTabSuffixItems(items);
  }, []);

  const setDetailBreadcrumbSuffix = useCallback((items: BreadcrumbItem[]) => {
    setDetailSuffixItems(items);
  }, []);

  const clearBreadcrumbSuffix = useCallback(() => {
    setTabSuffixItems([]);
    setDetailSuffixItems([]);
  }, []);

  const setBreadcrumbSuffix = useCallback((items: BreadcrumbItem[]) => {
    setTabSuffixItems(items);
    setDetailSuffixItems([]);
  }, []);

  const suffixItems = useMemo(
    () => [...tabSuffixItems, ...detailSuffixItems],
    [tabSuffixItems, detailSuffixItems],
  );

  const value = useMemo(
    () => ({
      suffixItems,
      setTabBreadcrumbSuffix,
      setDetailBreadcrumbSuffix,
      setBreadcrumbSuffix,
      clearBreadcrumbSuffix,
    }),
    [
      suffixItems,
      setTabBreadcrumbSuffix,
      setDetailBreadcrumbSuffix,
      setBreadcrumbSuffix,
      clearBreadcrumbSuffix,
    ],
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumb = (): BreadcrumbContextValue => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumb deve ser usado dentro de BreadcrumbProvider');
  }
  return context;
};
